import React, { useReducer } from "react";
import type { FC } from "react";
import useTranslation from "next-translate/useTranslation";
import { isNonNullable } from "@/types/isNonNullable";
import type { MatchPredictionState } from "./utils/matchPrediction.types";
import { matchPredictionReducer, ActionTypes } from "./matchPredictionReducer";
import type { MatchPredictionComponent } from "@motain/xpa-proto-files-web/lib/types/match_prediction";
import { MatchPredictionVariant } from "./utils/matchPrediction.types";
import { shareableLink } from "./utils/shareableLink";
import { NativeShareComponent } from "@/xpa-components/native-share/NativeShare";
import { MatchPredictionService } from "./service/matchPrediction.service";
import { trackingService } from "@/modules/analytics";
import {
  ClientParameterType,
  EventType,
} from "@motain/xpa-proto-files-web/lib/types/tracking";
import { predictionsToPercentagePoints } from "./utils/predictionsToPercentagePoints";
import { isNullable } from "@/types/isNullable";
import { HeaderFC } from "./components/HeaderFC";
import { MatchPredictionButton } from "./components/MatchPredictionButton";
import { BasedPredictionWrapper } from "./components/BasedPredictionWrapper";
import styles from "./MatchPrediction.module.scss";
import { remoteLogger } from "@/utils/remoteLogger";
import { assertNever } from "@/utils/assertNever";
import { VisibilityAlert } from "@/components/visibility-alert/VisibilityAlert";

const matchPredictionService = new MatchPredictionService();

const handleMatchPredictionInCookie = (
  matchId: string,
): MatchPredictionVariant | undefined => {
  return matchPredictionService.getPrediction(matchId);
};

export const MatchPrediction: FC<MatchPredictionComponent> = (props) => {
  const {
    matchId,
    title,
    subtitle,
    home,
    away,
    draw,
    totalNumOfVotes,
    trackingEvents,
    isPredictionAllowed,
    shareButton,
  } = props;

  const persistedUserPrediction = handleMatchPredictionInCookie(props.matchId);

  const visibleTrackingEvent = trackingEvents
    .filter((event) => event.type === EventType.EVENT_VISIBLE)
    .map((ev) => {
      const clientParameters = ev.clientParameterRequests.reduce<
        Record<string, string>
      >((record, parameter) => {
        if (
          parameter.type ===
          ClientParameterType.PARAMETER_MATCH_PREDICTION_USER_VOTED
        ) {
          record[parameter.key] = isNonNullable(persistedUserPrediction)
            ? "true"
            : "false";
        }
        return record;
      }, {});

      return {
        ...ev,
        // @TODO refactor this to properly handle client parameters
        serverParameters: {
          ...clientParameters,
        },
      };
    });

  const homePredictions = home?.predictions ?? 0;
  const awayPredictions = away?.predictions ?? 0;
  const drawPredictions = draw?.predictions ?? 0;

  const percentagePoints = predictionsToPercentagePoints({
    home: homePredictions,
    draw: drawPredictions,
    away: awayPredictions,
  });
  const initialState: MatchPredictionState = {
    title,
    subtitle,
    totalPredictions: homePredictions + drawPredictions + awayPredictions,
    shareButton: shareButton,
    totalOfVotes: totalNumOfVotes,
    persistedUserPrediction: persistedUserPrediction,
    home: {
      ...home,
      percentPoints: percentagePoints.home,
      predictions: homePredictions,
      trackingEvents: home?.trackingEvents,
    },
    away: {
      ...away,
      percentPoints: percentagePoints.away,
      predictions: awayPredictions,
      trackingEvents: away?.trackingEvents,
    },
    draw: {
      ...draw,
      percentPoints: percentagePoints.draw,
      predictions: drawPredictions,
      trackingEvents: draw?.trackingEvents,
    },
    trackEvents: trackingEvents,
  };

  const [state, dispatch] = useReducer(matchPredictionReducer, initialState);

  const [isClicked, setIsClicked] = useReducer(() => false, true);
  const isDisabled =
    !isPredictionAllowed || !isClicked || persistedUserPrediction !== undefined;
  const { t } = useTranslation("web-payments");

  if (isNullable(home) || isNullable(away) || isNullable(draw)) {
    return <></>;
  }

  const handlePrediction = (
    userSelectedPrediction: MatchPredictionVariant,
  ): void => {
    (async () => {
      dispatch({
        type: ActionTypes.FETCH_MATCH_PREDICTION_REQUEST,
        payload: userSelectedPrediction,
      });

      try {
        const predictions = await matchPredictionService.predict(
          matchId,
          userSelectedPrediction,
        );

        const matchName = `${home.name} vs ${away.name}`;
        const link = shareableLink();

        const shareableText = () => {
          switch (userSelectedPrediction) {
            case MatchPredictionVariant.Home:
              return t("MATCH_PREDICTION_SHARE_MESSAGE_WIN", {
                matchName,
                teamName: home.name,
                link,
              });

            case MatchPredictionVariant.Away:
              return t("MATCH_PREDICTION_SHARE_MESSAGE_WIN", {
                matchName,
                teamName: away.name,
                link,
              });

            case MatchPredictionVariant.Draw:
              return t("MATCH_PREDICTION_SHARE_MESSAGE_DRAW", {
                matchName,
                link,
              });

            default:
              return assertNever(userSelectedPrediction);
          }
        };

        const percentagePointsAfterPrediction =
          predictionsToPercentagePoints(predictions);

        dispatch({
          type: ActionTypes.FETCH_MATCH_PREDICTION_SUCCESS,
          payload: {
            ...initialState,
            home: {
              ...state.home,
              percentPoints: percentagePointsAfterPrediction.home,
              predictions: predictions.home,
            },
            away: {
              ...state.away,
              percentPoints: percentagePointsAfterPrediction.away,
              predictions: predictions.away,
            },
            draw: {
              ...state.draw,
              percentPoints: percentagePointsAfterPrediction.draw,
              predictions: predictions.draw,
            },
            shareText: shareableText(),
          },
        });

        setIsClicked();

        trackingService.sendXpaTracking(
          state.trackEvents,
          EventType.EVENT_CLICK,
        );
      } catch (error: unknown) {
        remoteLogger.error(error);
      }
    })().catch((error) => remoteLogger.error(error));
  };

  return (
    <section className={styles.wrapper}>
      {isNonNullable(title) && (
        <HeaderFC
          title={title}
          subtitle={subtitle}
          totalOfVotes={state.totalOfVotes}
        />
      )}

      <span className={styles.visitbleTracker}>
        <VisibilityAlert
          onVisible={() => {
            trackingService.sendXpaTracking(
              visibleTrackingEvent,
              EventType.EVENT_VISIBLE,
            );
          }}
        />
      </span>

      <ul className={styles.buttons}>
        <li className={styles.buttonsElement}>
          <MatchPredictionButton
            orientation="left"
            isDisabled={isDisabled}
            handleClick={() => {
              handlePrediction(MatchPredictionVariant.Home);
            }}
            team={state.home}
          />
        </li>
        <li className={styles.buttonsElement}>
          <MatchPredictionButton
            orientation="center"
            isDisabled={isDisabled}
            handleClick={() => {
              handlePrediction(MatchPredictionVariant.Draw);
            }}
            team={state.draw}
          />
        </li>
        <li className={styles.buttonsElement}>
          <MatchPredictionButton
            orientation="right"
            isDisabled={isDisabled}
            handleClick={() => {
              handlePrediction(MatchPredictionVariant.Away);
            }}
            team={state.away}
          />
        </li>
      </ul>
      <BasedPredictionWrapper
        isPredictionAllowed={isDisabled}
        totalPredictions={state.totalPredictions}
      >
        {isNonNullable(state.share) && (
          <NativeShareComponent {...state.share} />
        )}
      </BasedPredictionWrapper>
    </section>
  );
};
